






















































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ChannelOverlay from '@/components/channel/ChannelOverlay.vue';
import PartnerCreateSelector, {
  PartnersListDisplayKeysEnum
} from '@/components/partner/PartnerCreateSelector.vue';

import {
  Button,
  Collapse,
  CollapseItem,
  Form,
  FormItem,
  Input,
  Option,
  Select
} from 'element-ui';

// services
import { channelService } from '@/services/ChannelService';
import { partnerService } from '@/services/PartnerService.ts';

// types
import { FrontChannelTypeEnum } from '@/types/types';
import { ChannelFields, PartnerFields, PartnerMemberScopedInput } from '@/types/graphql/schemaTypes';

// rules
import rules from '@/rules/rules';

// gql
import fetchPartnersListQuery from '@/graphql/queries/partners/FetchPartnerList.gql';
import fetchPartnersQuery from '@/graphql/queries/partners/FetchPartners.gql';
import fetchChannelsQuery from '@/graphql/queries/channels/FetchChannels.gql';

@Component({
  apollo: {
    partnerOptions: {
      query: fetchPartnersListQuery,
      variables() {
        return {
          pagination: {
            page: 1,
            per: 1000
          }
        };
      },
      update(data) {
        return data?.partners;
      },
      fetchPolicy: 'cache-and-network',
      error() {
        this.$store.dispatch('Alert/addAlert', {
          type: 'error',
          message: this.$t('error.fetch'),
          time: 2000
        });
      }
    },
    selectedPartner: {
      query: fetchPartnersQuery,
      variables() {
        return {
          partnerId: this.channelFormData.partner
        };
      },
      skip() {
        return !this.channelFormData.partner;
      },
      update(data) {
        return data && data.partners[0];
      },
      fetchPolicy: 'cache-and-network'
    },
    channel: {
      query: fetchChannelsQuery,
      variables() {
        return {
          channelId: this.channelId
        };
      },
      skip() {
        return this.isCreation;
      },
      update(data) {
        const partnerMembers = data?.channels?.[0]?.partnerMembers;
        this.channelFormData.partner = partnerMembers?.[0]?.partner?.id;
        return data && data.channels[0];
      },
      fetchPolicy: 'network-only'
    }
  },
  components: {
    ChannelOverlay,
    PartnerCreateSelector,
    'el-button': Button,
    'el-collapse': Collapse,
    'el-collapse-item': CollapseItem,
    'el-form': Form,
    'el-form-item': FormItem,
    'el-input': Input,
    'el-option': Option,
    'el-select': Select
  }
})
export default class ChannelUpsert extends Vue {
  @Prop({ default: false, type: Boolean }) embeded!: boolean;
  // ChannelId is 'creation' in case of a creation
  @Prop({ required: true, type: String }) channelId!: string;

  // data
  private isRequesting = false;
  private addingMember = '';
  private newMemberId = null;

  // template
  // The type of the channel is creation in both edition and creation cases
  private readonly channelType = FrontChannelTypeEnum.creation;
  private readonly partnerKey = PartnersListDisplayKeysEnum.name;

  // gql
  private readonly partnerOptions: PartnerFields[];
  private selectedPartner: PartnerFields;
  private channel: ChannelFields;

  // Forms
  private channelFormData = {
    partner: null,
    members: []
  }

  private newMember: PartnerMemberScopedInput = {
    name: null,
    email: null,
    phoneNumber: null
  };

  private readonly rules = rules;

  $refs!: {
    memberForm: HTMLFormElement;
    channelForm: HTMLFormElement;
  };

  private get isCreation(): boolean {
    // For creation we use a special channelId 'creation'
    return this.channelId === this.channelType;
  }

  private get membersDisabled(): boolean {
    return !this.selectedPartner || !this.selectedPartner?.members?.length;
  }

  private get membersPlaceholder(): string {
    const isMembersEmpty = !this.selectedPartner?.members?.length;
    return isMembersEmpty ? this.$t('partners.members.none').toString()
      : this.$t('shared.choose').toString();
  }

  private alertError(): void {
    this.$store.dispatch('Alert/addAlert', {
      type: 'error',
      message: this.$t('error.push'),
      time: 2000
    });
  }

  private changePartner(): void {
    this.$apollo.queries.selectedPartner.refetch();
    this.channelFormData.members = [];
  }

  private async createChannel(): Promise<void> {
    this.$refs.channelForm.validate(async(valid: boolean) => {
      if (valid && !this.isRequesting && this.$route?.params?.id) {
        try {
          this.isRequesting = true;

          const response = await channelService.createPartnerMembersChannel(
            this.channelFormData.members,
            this.$route.params.id
          );

          if (response && response.data) {
            const newChannelId = response.data?.createPartnerMembersChannel?.id;
            this.$root.$emit('channelCreated');
            this.$emit('close', this.channelId);
            this.$emit('openChannel', newChannelId);
          }
        } catch (error) {
          this.alertError();
        } finally {
          this.isRequesting = false;
        }
      }
    });
  }

  private async createMember(): Promise<void> {
    this.$refs.memberForm.validate(async(valid: boolean) => {
      if (valid && !this.isRequesting) {
        try {
          this.isRequesting = true;

          const response = await partnerService
            .createPartnerMember([this.newMember], this.channelFormData.partner);

          if (response) {
            this.$refs.memberForm.resetFields();
            this.$store.dispatch('Alert/addAlert', {
              type: 'success',
              message: this.$t('success.created'),
              time: 2000
            });
            this.newMemberId = response.data?.createPartnerMember?.[0]?.id;
            this.$apollo.queries.selectedPartner.refetch();
          }
        } catch (error) {
          this.alertError();
        } finally {
          this.isRequesting = false;
        }
      }
    });
  }

  private async editChannel(): Promise<void> {
    this.$refs.channelForm.validate(async(valid: boolean) => {
      if (valid && !this.isRequesting && this.$route?.params?.id) {
        try {
          this.isRequesting = true;

          const response = await channelService.updateChannel(
            this.channelId,
            this.channelFormData.members
          );

          if (response && response.data) {
            this.$store.dispatch('Alert/addAlert', {
              type: 'success',
              message: this.$t('success.updated'),
              time: 2000
            });
            this.$root.$emit('channelCreated');
            this.$emit('close');
          }
        } catch (error) {
          this.alertError();
        } finally {
          this.isRequesting = false;
        }
      }
    });
  }

  private upsertChannel() {
    if (this.isCreation) {
      this.createChannel();
    } else {
      this.editChannel();
    }
  }

  @Watch('selectedPartner')
  private pushMemberAfterUpdate(): void {
    if (this.newMemberId) {
      this.channelFormData.members.push(this.newMemberId);
      this.newMemberId = null;
    } else if (!this.isCreation) {
      this.channelFormData.members = [];
      this.channel.partnerMembers.forEach(member => {
        this.channelFormData.members.push(member.id);
      });
    }
  }
}
